import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MyContext } from "../MainContext";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPaperPlane, faChevronLeft, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from 'react-bootstrap';
import Header from '../components/header';
import Footer from '../components/footer';
import LoadingScreen from '../components/loading_screen';
import { timeAgo } from '../utils/functions';
import { backendUrl } from '../utils/variables';


export default function MessagesPage() {
    const { user } = useContext(MyContext);
    const navigate = useNavigate();
    
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [messageRequestsCount, setMessageRequestsCount] = useState(0);

    const API_BASE = backendUrl;


    useEffect(() => {
        if (user && user.user_id) {
            fetchConversations();
            fetchMessageRequestsCount();
            
            // Refresh conversations every 30 seconds
            const interval = setInterval(() => {
                fetchConversations();
                fetchMessageRequestsCount();
            }, 30000);
            
            return () => clearInterval(interval);
        }
    }, [user]);

    const fetchConversations = async () => {
        if (!user || !user.user_id) return;

        try {
            const response = await fetch(`${API_BASE}/get_conversations.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id
                }),
            });

            const data = await response.json();
            if (data.success) {
                setConversations(data.conversations || []);
            }
        } catch (error) {
            console.error('Error fetching conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMessageRequestsCount = async () => {
        if (!user || !user.user_id) return;

        try {
            const response = await fetch(`${API_BASE}/get_message_requests.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id
                }),
            });

            const data = await response.json();
            if (data.success) {
                setMessageRequestsCount(data.requests?.length || 0);
            }
        } catch (error) {
            console.error('Error fetching message requests count:', error);
        }
    };

    const handleConversationClick = (otherUserId) => {
        navigate(`/messages/${otherUserId}`);
    };

    if (!user || !user.user_id) {
        return <LoadingScreen />;
    }

    return (
        <div className="container">
            <Header />
            
            <div className="container-fluid mt-5 pt-3">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4>Messages</h4>
                            <button 
                                className="btn btn-link position-relative"
                                onClick={() => navigate('/messages/requests')}
                                style={{ color: '#48887b' }}
                            >
                                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#48887b' }} />
                                {messageRequestsCount > 0 && (
                                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                                        {messageRequestsCount > 99 ? '99+' : messageRequestsCount}
                                    </span>
                                )}
                            </button>
                        </div>

                        {loading ? (
                            <LoadingScreen />
                        ) : conversations.length === 0 ? (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faEnvelope} size="3x" className="text-muted mb-3" />
                                <p className="text-muted">No conversations yet</p>
                                <p className="text-muted">Start a conversation by sending a message request to someone you follow</p>
                            </div>
                        ) : (
                            <div className="list-group">
                                {conversations.map((conv) => (
                                    <div
                                        key={conv.other_user_id}
                                        className="list-group-item list-group-item-action"
                                        onClick={() => handleConversationClick(conv.other_user_id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <img
                                                src={conv.other_photo || `${process.env.PUBLIC_URL}/avatar.jpg`}
                                                alt={conv.other_username}
                                                className="rounded-circle me-3"
                                                width="50"
                                                height="50"
                                            />
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <h6 className="mb-0">
                                                            {conv.other_first_name} {conv.other_last_name}
                                                        </h6>
                                                        <small className="text-muted">@{conv.other_username}</small>
                                                    </div>
                                                    {conv.unread_count > 0 && (
                                                        <span className="badge rounded-pill" style={{ backgroundColor: '#48887b' }}>
                                                            {conv.unread_count > 99 ? '99+' : conv.unread_count}
                                                        </span>
                                                    )}
                                                </div>
                                                {conv.last_message && (
                                                    <p className="mb-0 mt-1 text-muted" style={{ fontSize: '0.9rem' }}>
                                                        {conv.last_message.length > 50 
                                                            ? conv.last_message.substring(0, 50) + '...' 
                                                            : conv.last_message}
                                                    </p>
                                                )}
                                                {conv.last_message_time && (
                                                    <small className="text-muted">
                                                        {timeAgo(conv.last_message_time)}
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Footer page="messages" />
        </div>
    );
}

